import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

// ─────────────────────────────────────────────────────────────────────────────
// Prospect Pages (admin): create personalised, trackable "your market, mapped"
// pages — the homepage experience made about one prospect's market. Each page
// gets an unguessable share link and records opens, unique visitors, time on
// page, section reach and CTA clicks.
// ─────────────────────────────────────────────────────────────────────────────

type Deck = {
  id: number; slug: string; title: string;
  prospect_name: string | null; prospect_company: string | null;
  market_name: string; region: string | null;
  candidate_count: number | null; company_count: number | null; manager_count: number | null;
  candidate_titles: string[]; company_names: string[]; manager_titles: string[];
  intro_message: string | null; cta_url: string | null; is_active: boolean;
  headline: string | null; custom_slug: string | null;
  created_at: string; unique_visitors: number; opens: number; total_seconds: number;
  last_activity: string | null;
};

// Pages report scroll depth quartiles; older decks reported named sections.
const SECTION_LABELS: Record<string, string> = {
  'depth-25': 'Scrolled 25%', 'depth-50': 'Scrolled 50%',
  'depth-75': 'Scrolled 75%', 'depth-100': 'Scrolled to the end',
  hero: 'Hero (their market)', what: 'What you get', signals: 'Monitoring signals',
  how: 'How it starts', cta: 'Call to action',
};
const SECTION_ORDER = ['depth-25', 'depth-50', 'depth-75', 'depth-100', 'hero', 'what', 'signals', 'how', 'cta'];

const emptyForm = {
  title: '', prospectName: '', prospectCompany: '', marketName: '', region: '',
  candidateCount: '', companyCount: '', managerCount: '',
  candidateTitles: '', companyNames: '', managerTitles: '',
  introMessage: '', ctaUrl: '', headline: '', customSlug: '',
};

const PitchDecks: React.FC = () => {
  const { user, token } = useAuth();
  const auth = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [decks, setDecks] = useState<Deck[]>([]);
  const [form, setForm] = useState<any>(null); // null = closed; {...emptyForm, id?} = open
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<{ deck: Deck; data: any } | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [genDesc, setGenDesc] = useState('');
  const [generating, setGenerating] = useState(false);

  // Link-to-prospect type-ahead (edit modal), scoped to a chosen project.
  // The scope is remembered so it only needs setting once (e.g. 514).
  const [linkQ, setLinkQ] = useState('');
  const [linkResults, setLinkResults] = useState<any[]>([]);
  const [linkProjects, setLinkProjects] = useState<any[]>([]);
  const [linkProjectId, setLinkProjectId] = useState<string>(() => {
    try { return localStorage.getItem('mm_prospect_link_project') || ''; } catch { return ''; }
  });
  const changeLinkProject = (id: string) => {
    setLinkProjectId(id);
    setLinkResults([]);
    try { localStorage.setItem('mm_prospect_link_project', id); } catch { /* convenience only */ }
  };
  useEffect(() => {
    if (!form) return;
    if (linkProjects.length === 0) {
      fetch('/api/projects', { headers: auth })
        .then(r => (r.ok ? r.json() : []))
        .then(p => Array.isArray(p) && setLinkProjects(p))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);
  useEffect(() => {
    if (linkQ.trim().length < 2) { setLinkResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const scope = linkProjectId ? `&projectId=${linkProjectId}` : '';
        const res = await fetch(`/api/pitch-decks/candidate-search?q=${encodeURIComponent(linkQ.trim())}${scope}`, { headers: auth });
        const data = await res.json();
        if (res.ok) setLinkResults(data.candidates || []);
      } catch { /* type-ahead is best-effort */ }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkQ, linkProjectId]);

  // Bulk creation from a project's candidates.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkProjects, setBulkProjects] = useState<any[]>([]);
  const [bulkProjectId, setBulkProjectId] = useState<string>('');
  const [bulkCands, setBulkCands] = useState<any[]>([]);
  const [bulkSelected, setBulkSelected] = useState<Set<number>>(new Set());
  const [bulkFilter, setBulkFilter] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkOp, setBulkOp] = useState<any>(null); // progress object while running

  const load = () => {
    fetch('/api/pitch-decks', { headers: auth })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(d => setDecks(d.decks))
      .catch(() => setMessage('Failed to load prospect pages.'));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  // Mark this browser as internal across *.marketmapr.com: your own visits
  // to prospect pages then skip the open-alert email and stay out of the
  // analytics. Any browser that has opened this dashboard is covered.
  useEffect(() => {
    try {
      if (window.location.hostname.endsWith('marketmapr.com')) {
        document.cookie = 'mm_internal=1; domain=.marketmapr.com; path=/; max-age=31536000; SameSite=Lax; Secure';
      }
    } catch { /* cookie is a convenience, never an error */ }
  }, []);

  if (user && user.id !== 1 && (user as any).role !== 'admin') {
    return <div className="p-10 text-gray-500">Not available.</div>;
  }

  // Always the www domain: the page is the real homepage, whose inline
  // animation scripts the app domain's CSP would block.
  const deckUrl = (d: Deck) => d.custom_slug
    ? `https://www.marketmapr.com/${d.custom_slug}`
    : `https://www.marketmapr.com/api/deck/${d.slug}`;

  const openEdit = (d?: Deck) => {
    if (!d) { setForm({ ...emptyForm }); return; }
    setForm({
      id: d.id, title: d.title, prospectName: d.prospect_name || '', prospectCompany: d.prospect_company || '',
      candidateId: (d as any).candidate_id || null, candidateLabel: (d as any).candidate_name || '',
      marketName: d.market_name, region: d.region || '',
      candidateCount: d.candidate_count ?? '', companyCount: d.company_count ?? '', managerCount: d.manager_count ?? '',
      candidateTitles: (d.candidate_titles || []).join(', '),
      companyNames: (d.company_names || []).join(', '),
      manageTitles: undefined,
      managerTitles: (d.manager_titles || []).join(', '),
      introMessage: d.intro_message || '', ctaUrl: d.cta_url || '',
      headline: d.headline || '', customSlug: d.custom_slug || '',
      cards: (d as any).cards || null,
    });
  };

  const generate = async () => {
    if (!genDesc.trim()) return;
    setGenerating(true); setMessage(null);
    try {
      const res = await fetch('/api/pitch-decks/generate', {
        method: 'POST', headers: auth, body: JSON.stringify({ description: genDesc }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Generation failed');
      const g = data.draft;
      setForm((f: any) => ({
        ...f,
        // A website/bio brief can identify the agency — fill these only if
        // the admin hasn't typed them already.
        prospectCompany: f.prospectCompany || g.prospectCompany || '',
        customSlug: f.customSlug || String(g.suggestedSlug || '').toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40),
        cards: (g.candidateCards || g.companyCards || g.managerCards)
          ? { candidates: g.candidateCards || [], companies: g.companyCards || [], managers: g.managerCards || [], demo: g.demo || null }
          : f.cards,
        marketName: g.marketName || f.marketName,
        headline: g.headline || f.headline,
        // Personal note stays empty unless the admin writes one — the
        // homepage's own lede reads better than generated filler.
        candidateTitles: (g.candidateTitles || []).join(', '),
        companyNames: (g.companyNames || []).join(', '),
        managerTitles: (g.managerTitles || []).join(', '),
        candidateCount: g.candidateCount ?? f.candidateCount,
        companyCount: g.companyCount ?? f.companyCount,
        managerCount: g.managerCount ?? f.managerCount,
      }));
    } catch (e: any) { setMessage(e.message); }
    finally { setGenerating(false); }
  };

  const save = async () => {
    setSaving(true); setMessage(null);
    try {
      const res = await fetch(form.id ? `/api/pitch-decks/${form.id}` : '/api/pitch-decks', {
        method: form.id ? 'PUT' : 'POST', headers: auth, body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setForm(null); load();
      setMessage(form.id ? 'Page updated.' : 'Page created — copy the link and send it.');
    } catch (e: any) { setMessage(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (d: Deck) => {
    if (!window.confirm(`Delete "${d.title}"? The share link will stop working.`)) return;
    await fetch(`/api/pitch-decks/${d.id}`, { method: 'DELETE', headers: auth });
    load();
  };

  const openAnalytics = async (d: Deck) => {
    const res = await fetch(`/api/pitch-decks/${d.id}/analytics`, { headers: auth });
    if (res.ok) setAnalytics({ deck: d, data: await res.json() });
  };

  const openBulk = async () => {
    setBulkOpen(true); setBulkOp(null); setBulkCands([]); setBulkSelected(new Set()); setBulkFilter('');
    try {
      const res = await fetch('/api/projects', { headers: auth });
      if (res.ok) setBulkProjects(await res.json());
    } catch { /* project list is a convenience */ }
  };

  const loadBulkCandidates = async (projectId: string) => {
    setBulkProjectId(projectId); setBulkCands([]); setBulkSelected(new Set());
    if (!projectId) return;
    setBulkLoading(true);
    try {
      const res = await fetch(`/api/pitch-decks/bulk-candidates?projectId=${projectId}`, { headers: auth });
      const data = await res.json();
      if (res.ok) setBulkCands(data.candidates || []);
    } finally { setBulkLoading(false); }
  };

  const toggleBulk = (id: number) => {
    setBulkSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 25) next.add(id);
      return next;
    });
  };

  const startBulk = async () => {
    if (!bulkProjectId || bulkSelected.size === 0) return;
    const res = await fetch('/api/pitch-decks/bulk-generate', {
      method: 'POST', headers: auth,
      body: JSON.stringify({ projectId: parseInt(bulkProjectId), candidateIds: Array.from(bulkSelected) }),
    });
    const data = await res.json();
    if (!res.ok) { setMessage(data.error || 'Bulk generation failed to start'); return; }
    setBulkOp({ status: 'processing', total: data.total, done: 0, created: 0, skipped: 0, failed: 0, results: [] });
    const poll = setInterval(async () => {
      try {
        const p = await fetch(`/api/pitch-decks/bulk-progress/${data.operationId}`, { headers: auth });
        if (!p.ok) return;
        const op = await p.json();
        setBulkOp(op);
        if (op.status === 'completed') { clearInterval(poll); load(); }
      } catch { /* keep polling */ }
    }, 2500);
  };

  const copy = (d: Deck) => {
    navigator.clipboard?.writeText(deckUrl(d));
    setCopiedId(d.id); setTimeout(() => setCopiedId(null), 1500);
  };

  const mins = (s: number) => (s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);

  const field = (label: string, key: string, placeholder = '', textarea = false) => (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
      {textarea ? (
        <textarea className="input text-sm w-full" rows={2} value={form[key]} placeholder={placeholder}
          onChange={e => setForm({ ...form, [key]: e.target.value })} />
      ) : (
        <input className="input text-sm w-full" value={form[key]} placeholder={placeholder}
          onChange={e => setForm({ ...form, [key]: e.target.value })} />
      )}
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-6">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Prospect Pages</h1>
          <p className="text-sm text-gray-500">Personalised "your market, mapped" pages with open &amp; engagement tracking.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={openBulk} className="px-4 py-2 border border-violet-300 text-violet-700 bg-violet-50 rounded-lg text-sm hover:bg-violet-100">
            ⚡ Bulk create
          </button>
          <button onClick={() => openEdit()} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
            + New prospect page
          </button>
        </div>
      </div>

      {message && <div className="mb-4 text-sm text-blue-800 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">{message}</div>}

      <div className="space-y-3">
        {decks.map(d => (
          <div key={d.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-gray-900">{d.title}</span>
                {!d.is_active && <span className="px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-500 rounded">inactive</span>}
              </div>
              <div className="text-xs text-gray-500 mt-0.5 truncate">
                {(d as any).candidate_name ? `🔗 ${(d as any).candidate_name} · ` : ''}{d.prospect_name || d.prospect_company || 'No prospect'} · {d.market_name}
                {d.last_activity && <> · last viewed {new Date(d.last_activity).toLocaleDateString('en-GB')}</>}
              </div>
            </div>
            <div className="flex items-center gap-5 text-center text-xs text-gray-600">
              <div><div className="text-lg font-bold text-gray-900">{d.opens}</div>opens</div>
              <div><div className="text-lg font-bold text-gray-900">{d.unique_visitors}</div>visitors</div>
              <div><div className="text-lg font-bold text-gray-900">{mins(d.total_seconds || 0)}</div>total time</div>
            </div>
            <div className="flex items-center gap-2">
              <a href={deckUrl(d)} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">Preview</a>
              <button onClick={() => copy(d)} className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">
                {copiedId === d.id ? 'Copied ✓' : 'Copy link'}
              </button>
              <button onClick={() => openAnalytics(d)} className="px-3 py-1.5 text-xs bg-violet-50 text-violet-700 border border-violet-200 rounded-lg hover:bg-violet-100">Analytics</button>
              <button onClick={() => openEdit(d)} className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">Edit</button>
              <button onClick={() => remove(d)} className="px-2 py-1.5 text-xs text-red-500 hover:text-red-700">✕</button>
            </div>
          </div>
        ))}
        {decks.length === 0 && (
          <div className="text-center text-gray-400 py-16 border border-dashed border-gray-200 rounded-xl">
            No prospect pages yet. Create one, copy the link, and send it to a prospect —
            you'll see when they open it and what they looked at.
          </div>
        )}
      </div>

      {/* Create / edit modal */}
      {form && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{form.id ? 'Edit prospect page' : 'New prospect page'}</h3>

            {/* Claude-assisted drafting */}
            <div className="mb-4 rounded-lg border border-violet-200 bg-violet-50 p-3">
              <label className="block text-xs font-semibold text-violet-800 mb-1">✨ Paste their website URL, their bio, or describe the market — Claude drafts the page</label>
              <div className="flex gap-2">
                <input className="input text-sm flex-1" value={genDesc}
                  placeholder='e.g. "abcrecruitment.com" or "Python developers in the Nordics, mostly fintech"'
                  onChange={e => setGenDesc(e.target.value)} />
                <button onClick={generate} disabled={generating || !genDesc.trim()}
                  className="px-3 py-1.5 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-40 whitespace-nowrap">
                  {generating ? 'Drafting…' : 'Generate'}
                </button>
              </div>
              <p className="text-[11px] text-violet-700 mt-1">Fills the fields below — edit anything before creating.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {field('Internal title *', 'title', 'e.g. Glynn Search — NZ architecture')}
              {field('Market name *', 'marketName', 'e.g. NZ architecture market')}
              {field('Prospect first name', 'prospectName', 'Paul')}
              {field('Prospect company', 'prospectCompany', 'Glynn Search')}

              {/* Link the page to the person it was created for — powers the
                  👁 indicator in the data table and {{prospectPage}} in lemlist. */}
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Link to prospect (person in your projects)</label>
                {form.candidateId ? (
                  <div className="flex items-center gap-2 text-sm bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    <span className="text-emerald-800">🔗 Linked to <b>{form.candidateLabel || `candidate #${form.candidateId}`}</b></span>
                    <button
                      onClick={() => setForm((f: any) => ({ ...f, candidateId: null, candidateLabel: '' }))}
                      className="ml-auto text-xs text-emerald-700 hover:text-emerald-900 underline"
                    >Unlink</button>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="flex gap-2">
                      <select className="input text-sm w-44 flex-shrink-0" value={linkProjectId}
                        onChange={e => changeLinkProject(e.target.value)}
                        title="Which project to search — remembered for next time">
                        <option value="">All projects</option>
                        {linkProjects.map((p: any) => (
                          <option key={p.id} value={p.id}>{p.name} (#{p.id})</option>
                        ))}
                      </select>
                      <input className="input text-sm flex-1" placeholder="Type their name to search…"
                        value={linkQ} onChange={e => setLinkQ(e.target.value)} />
                    </div>
                    {linkResults.length > 0 && (
                      <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                        {linkResults.map((c: any) => (
                          <button key={c.id}
                            onClick={() => { setForm((f: any) => ({ ...f, candidateId: c.id, candidateLabel: c.name })); setLinkQ(''); setLinkResults([]); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0">
                            <span className="text-gray-900">{c.name}</span>
                            <span className="text-xs text-gray-500 ml-2">{c.current_company || ''} · {c.project_name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {field('Region', 'region', 'New Zealand')}
              {field('CTA link', 'ctaUrl', 'https://calendly.com/…')}
              {field('Candidate count', 'candidateCount', '3186')}
              {field('Company count', 'companyCount', '412')}
              {field('Hiring manager count', 'managerCount', '780')}
              <div className="col-span-2">
                {field('Headline override (title & share preview)', 'headline', 'The whole Python developer market, mapped in 24 hours.')}
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Custom link (optional)</label>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-gray-400 whitespace-nowrap">www.marketmapr.com/</span>
                  <input className="input text-sm flex-1" value={form.customSlug} placeholder="abcrecruitment"
                    onChange={e => setForm({ ...form, customSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 mt-3">
              {field('Sample candidate job titles (comma-separated)', 'candidateTitles', 'Senior Architect, Design Manager, BIM Lead', true)}
              {field('Sample companies (comma-separated)', 'companyNames', 'Warren & Mahoney, Jasmax, Architectus', true)}
              {field('Sample hiring manager titles (comma-separated)', 'managerTitles', 'Practice Director, Head of Design', true)}
              {field('Personal intro line (optional)', 'introMessage', "Following our chat — here's what your market would look like in MarketMapr.", true)}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setForm(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={save} disabled={saving || !form.title.trim() || !form.marketName.trim()}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40">
                {saving ? 'Saving…' : form.id ? 'Save changes' : 'Create page'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Analytics drawer */}
      {analytics && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setAnalytics(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900">{analytics.deck.title}</h3>
            <p className="text-xs text-gray-500 mb-4">{analytics.deck.market_name}</p>
            <div className="grid grid-cols-4 gap-3 text-center mb-5">
              {[
                ['Opens', analytics.data.totals.opens],
                ['Visitors', analytics.data.totals.unique_visitors],
                ['Total time', mins(parseInt(analytics.data.totals.total_seconds) || 0)],
                ['CTA clicks', analytics.data.totals.cta_clicks],
              ].map(([l, v]) => (
                <div key={l as string} className="bg-gray-50 rounded-lg py-3">
                  <div className="text-xl font-bold text-gray-900">{v as any}</div>
                  <div className="text-[11px] text-gray-500">{l}</div>
                </div>
              ))}
            </div>
            <h4 className="text-sm font-semibold text-gray-800 mb-2">How far visitors got</h4>
            <div className="space-y-1.5 mb-5">
              {SECTION_ORDER.filter(sec =>
                analytics.data.sections.some((s: any) => s.section === sec)
              ).map(sec => {
                const row = analytics.data.sections.find((s: any) => s.section === sec);
                const reached = row ? parseInt(row.sessions_reached) : 0;
                const max = Math.max(1, parseInt(analytics.data.totals.unique_visitors) || 1);
                return (
                  <div key={sec} className="flex items-center gap-2 text-xs">
                    <span className="w-36 text-gray-600">{SECTION_LABELS[sec]}</span>
                    <div className="flex-1 bg-gray-100 rounded h-4 overflow-hidden">
                      <div className="h-4 bg-blue-500" style={{ width: `${Math.min(100, (reached / max) * 100)}%` }} />
                    </div>
                    <span className="w-8 text-right text-gray-700 font-medium">{reached}</span>
                  </div>
                );
              })}
            </div>
            <h4 className="text-sm font-semibold text-gray-800 mb-2">Recent visits</h4>
            <div className="divide-y divide-gray-100">
              {analytics.data.sessions.map((s: any) => (
                <div key={s.session_id} className="py-2 text-xs flex items-center justify-between">
                  <span className="text-gray-600">{new Date(s.started).toLocaleString('en-GB')}</span>
                  <span className="text-gray-700">{mins(parseInt(s.seconds) || 0)} on page{s.clicked_cta ? ' · 🎯 clicked CTA' : ''}</span>
                </div>
              ))}
              {analytics.data.sessions.length === 0 && <div className="py-4 text-xs text-gray-400">No visits yet.</div>}
            </div>
          </div>
        </div>
      )}

      {/* Bulk creation from a project's candidates */}
      {bulkOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setBulkOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[88vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-semibold text-gray-900">⚡ Bulk create prospect pages</h3>
              <button onClick={() => setBulkOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Pick a project of prospects — each selected person's own profile (bio, company, website) is the brief.
              Pages are created linked to the person, so they flow into campaigns and lemlist as <code className="bg-gray-100 px-1 rounded">{'{{prospectPage}}'}</code>.
            </p>

            {!bulkOp && (
              <>
                <select className="input text-sm w-full mb-3" value={bulkProjectId} onChange={e => loadBulkCandidates(e.target.value)}>
                  <option value="">Choose a project…</option>
                  {bulkProjects.map((p: any) => (
                    <option key={p.id} value={p.id}>{p.name} (#{p.id})</option>
                  ))}
                </select>

                {bulkLoading && <p className="text-sm text-gray-500 py-3">Loading candidates…</p>}

                {bulkCands.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <input className="input text-sm flex-1" placeholder="Filter by name or company…"
                        value={bulkFilter} onChange={e => setBulkFilter(e.target.value)} />
                      <span className="text-xs text-gray-500 whitespace-nowrap">{bulkSelected.size}/25 selected</span>
                    </div>
                    <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-72 overflow-y-auto">
                      {bulkCands
                        .filter((c: any) => !bulkFilter || `${c.name} ${c.current_company}`.toLowerCase().includes(bulkFilter.toLowerCase()))
                        .map((c: any) => (
                          <label key={c.id} className={`flex items-center gap-3 px-3 py-2 ${c.deck_id ? 'opacity-50' : 'hover:bg-gray-50 cursor-pointer'}`}>
                            <input type="checkbox" disabled={!!c.deck_id} checked={bulkSelected.has(c.id)} onChange={() => toggleBulk(c.id)} />
                            <div className="min-w-0 flex-1">
                              <span className="text-sm text-gray-900">{c.name}</span>
                              <span className="text-xs text-gray-500 ml-2">{c.current_company || c.headline || ''}</span>
                            </div>
                            {c.deck_id && <span className="text-[10px] uppercase font-semibold text-emerald-600">has page</span>}
                          </label>
                        ))}
                    </div>
                    <button onClick={startBulk} disabled={bulkSelected.size === 0}
                      className="mt-4 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm hover:bg-violet-700 disabled:opacity-40">
                      Generate {bulkSelected.size || ''} page{bulkSelected.size === 1 ? '' : 's'}
                    </button>
                  </>
                )}
              </>
            )}

            {bulkOp && (
              <div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="font-medium text-gray-800">
                    {bulkOp.status === 'completed' ? 'Done' : `Generating… ${bulkOp.current ? `(${bulkOp.current})` : ''}`}
                  </span>
                  <span className="text-gray-500">{bulkOp.done}/{bulkOp.total}</span>
                </div>
                <div className="bg-gray-100 rounded h-2 overflow-hidden mb-4">
                  <div className="h-2 bg-violet-500 transition-all" style={{ width: `${(bulkOp.done / Math.max(1, bulkOp.total)) * 100}%` }} />
                </div>
                <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl max-h-72 overflow-y-auto">
                  {(bulkOp.results || []).map((r: any) => (
                    <div key={r.candidateId} className="px-3 py-2 flex items-center justify-between text-sm">
                      <span className="text-gray-800">{r.name}</span>
                      {r.status === 'created' && <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">View page →</a>}
                      {r.status === 'skipped' && <span className="text-xs text-gray-400">{r.reason}</span>}
                      {r.status === 'failed' && <span className="text-xs text-red-500" title={r.reason}>failed</span>}
                    </div>
                  ))}
                </div>
                {bulkOp.status === 'completed' && (
                  <p className="text-xs text-gray-500 mt-3">
                    {bulkOp.created} created · {bulkOp.skipped} skipped · {bulkOp.failed} failed. Each generation takes ~15-30s; pages appear in the list as they finish.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PitchDecks;
